// src/pages/league-dashboard/MessageBoard.js
import React, { useState, useEffect } from 'react';
import { MessageSquare, RefreshCw, AlertCircle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import MessageComposer from '../../components/message-board/MessageComposer';
import MessagePost from '../../components/message-board/MessagePost';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';

const MessageBoard = ({ league, leagueId, user, userTeam, leagueBanner, bannerConfig, hideBanner = false }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total_messages: 0, unread_messages: 0 });
  const [refreshing, setRefreshing] = useState(false);

  const isCommissioner = league?.role === 'commissioner';

  // Load messages and stats
  useEffect(() => {
    if (leagueId) {
      loadMessages();
      loadStats();
    }
  }, [leagueId]);

  const loadMessages = async (showRefreshing = false) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await leaguesAPI.getMessages(leagueId, 50, 0, true);
      
      if (response.success) {
        setMessages(response.messages || []);
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await leaguesAPI.getMessageStats(leagueId);
      if (response.success) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Error loading message stats:', err);
    }
  };

  const handlePostMessage = async (messageData) => {
    try {
      const response = await leaguesAPI.postMessage(leagueId, messageData);
      
      if (response.success) {
        // Reload messages to get the new one
        await loadMessages(true);
        await loadStats();
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to post message');
    }
  };

  const handleReply = async (parentMessageId, replyData) => {
    try {
      const response = await leaguesAPI.postMessage(leagueId, {
        ...replyData,
        parent_message_id: parentMessageId
      });
      
      if (response.success) {
        await loadMessages(true);
        await loadStats();
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to post reply');
    }
  };

  const handleEdit = async (messageId, editData) => {
    try {
      const response = await leaguesAPI.updateMessage(leagueId, messageId, editData);
      
      if (response.success) {
        await loadMessages(true);
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update message');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const response = await leaguesAPI.deleteMessage(leagueId, messageId);
      
      if (response.success) {
        await loadMessages(true);
        await loadStats();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleTogglePin = async (messageId, isPinned) => {
    try {
      // Ensure isPinned is a boolean
      const pinState = Boolean(isPinned);
      console.log('Toggling pin:', { messageId, currentState: !pinState, newState: pinState });
      
      const response = await leaguesAPI.togglePinMessage(leagueId, messageId, pinState);
      
      if (response.success) {
        console.log('Pin toggle successful, reloading messages...');
        // Small delay to ensure database is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        await loadMessages(true);
      } else {
        alert('Failed to toggle pin: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
      alert(err?.response?.data?.detail || err?.message || 'Failed to toggle pin. Please try again.');
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="flex items-center space-x-3">
          <div 
            className={`w-8 h-8 border-2 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`}
          />
          <span className={`${dynastyTheme.classes.text.white} text-lg`}>Loading messages...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner Header */}
      {!hideBanner && (
      <LeagueBanner
        leagueBanner={leagueBanner}
        title="League Message Board"
        subtitle={`${stats.total_messages} total messages${stats.unread_messages > 0 ? ` • ${stats.unread_messages} unread` : ''}`}
        minHeight="160px"
        bannerConfig={bannerConfig}
        actions={
          <button
            onClick={() => loadMessages(true)}
            disabled={refreshing}
            className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'sm')} flex items-center gap-2`}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        }
      />
      )}

      {/* Error Display */}
      {error && (
        <div className={`p-4 rounded-lg bg-red-500/20 text-red-400 flex items-center gap-3`}>
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Message Composer */}
      <div>
        <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white} mb-2`}>
          Post a Message
        </h3>
        <MessageComposer 
          leagueId={leagueId}
          onSubmit={handlePostMessage} 
          allowImageUpload={true}
        />
      </div>

      {/* Messages List */}
      <div>
        <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white} mb-2`}>
          Messages
        </h3>
        
        {messages.length === 0 ? (
          <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
            <MessageSquare className={`w-16 h-16 mx-auto mb-4 opacity-50 ${dynastyTheme.classes.text.neutralLight}`} />
            <h4 className={`${dynastyTheme.classes.text.white} font-semibold mb-2`}>
              No messages yet
            </h4>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Be the first to post a message to your league!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <MessagePost
                key={message.message_id}
                message={message}
                leagueId={leagueId}
                currentUserId={user?.user_id}
                isCommissioner={isCommissioner}
                onReply={handleReply}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTogglePin={handleTogglePin}
                showReplies
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBoard;
